'use client';

import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { Skeleton } from '@mantine/core';
import cn from 'classnames';
import Image from 'next/image';
import { useState, useMemo, useEffect } from 'react';
import { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import Button from '@/components/Button';
import { ProductGalleryProps } from '@/components/ProductGallery/types';

import styles from './styles.module.scss';
import { Props } from './types';

const ProductImages = ({
  setSwiperRef,
  parentSwiperRef,
  images,
  title,
  thumbnail,
}: Props & ProductGalleryProps) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const imagesWithThumbnail = useMemo(() => {
    if (!thumbnail) return images;
    return [{ url: thumbnail }, ...images];
  }, [images, thumbnail]);

  const handleThumbnailClick = (index: number) => {
    if (parentSwiperRef) parentSwiperRef.slideTo(index);
    setActiveImageIndex(index);
  };

  useEffect(() => {
    if (parentSwiperRef) {
      parentSwiperRef.on('slideChange', (swiper: SwiperType) => {
        setActiveImageIndex(swiper.activeIndex);
      });
    }
  });

  return (
    <div className={styles.container}>
      <Button variant="icon" className="product-images-button-prev">
        <ChevronUpIcon />
      </Button>
      <Swiper
        modules={[Navigation]}
        navigation={{
          nextEl: '.product-images-button-next',
          prevEl: '.product-images-button-prev',
        }}
        slidesPerView={4}
        spaceBetween={10}
        direction="vertical"
        style={{ height: '500px' }}
        onSwiper={(swiper: SwiperType) => setSwiperRef && setSwiperRef(swiper)}
        onSlideChange={(swiper: SwiperType) => {
          setActiveImageIndex(swiper.activeIndex);
          if (parentSwiperRef) parentSwiperRef.slideTo(swiper.activeIndex);
        }}
      >
        {imagesWithThumbnail.map((image, index) => (
          <SwiperSlide
            key={index}
            className={cn(styles.imageWrapper, {
              [styles.inactive]: index !== activeImageIndex,
            })}
          >
            <Skeleton
              visible={isLoading}
              style={{ width: '114px', height: '114px' }}
              width={114}
              height={114}
            >
              <Image
                src={image.url}
                alt={`Zdjęcie produktu ${title}`}
                width={114}
                height={114}
                style={{ cursor: 'pointer' }}
                onLoadingComplete={() => setIsLoading(false)}
                onClick={() => handleThumbnailClick(index)}
                priority
              />
            </Skeleton>
          </SwiperSlide>
        ))}
      </Swiper>
      <Button variant="icon" className="product-images-button-next">
        <ChevronDownIcon />
      </Button>
    </div>
  );
};

export default ProductImages;
